import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { StoreEmployee } from "@/models/storeModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Button } from "@/components/ui/button";
import { Pen, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface StaffTableProps {
  staff: StoreEmployee[];
  isAdminHQ: boolean;
  onDeleteEmployee: (employee: StoreEmployee) => void;
  onRoleChange: (employee: StoreEmployee, role: string) => Promise<void>;
}

export function StaffTable({
  staff,
  isAdminHQ,
  onDeleteEmployee,
  onRoleChange,
}: StaffTableProps) {
  const { stores } = useStoreManagementStore();
  const [updatingIds, setUpdatingIds] = useState<string[]>([]);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const { t } = useTranslation();

  // Get store names for display
  const getStoreName = (storeId: string) => {
    return (
      stores.find((store) => store.id === storeId)?.name || t("unknownStore")
    );
  };

  // Format date to a readable format
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format role to display with capitalized first letter
  const formatRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const handleRoleChange = (employee: StoreEmployee, role: string) => {
    setUpdatingIds((prev) => [...prev, employee.id]);

    // Now properly handling the Promise returned by onRoleChange
    onRoleChange(employee, role).finally(() => {
      setUpdatingIds((prev) => prev.filter((id) => id !== employee.id));
      setEditingRoleId(null); // Close edit mode after role change
    });
  };

  if (staff.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center p-8">
          <h3 className="text-lg font-semibold">
            {t("staffManagement.noStaffMembersFound")}
          </h3>
          <p className="text-muted-foreground mt-2">
            {t("staffManagement.inviteStaffMembers")}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("name")}</TableHead>
            <TableHead>{t("email")}</TableHead>
            <TableHead>{t("role")}</TableHead>
            <TableHead>{t("store")}</TableHead>
            <TableHead>{t("joined")}</TableHead>
            <TableHead>{t("address")}</TableHead>
            <TableHead className="text-right">{t("actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {staff.map((employee) => (
            <TableRow key={employee.id}>
              <TableCell>
                {employee.firstName} {employee.lastName}
              </TableCell>
              <TableCell>{employee.email || employee.userId}</TableCell>
              <TableCell>
                {editingRoleId === employee.id ? (
                  <Select
                    defaultValue={employee.role}
                    onValueChange={(value) => handleRoleChange(employee, value)}
                    disabled={updatingIds.includes(employee.id)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder={t("selectRole")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{t("admin")}</SelectItem>
                      <SelectItem value="manager">{t("manager")}</SelectItem>
                      <SelectItem value="staff">{t("staff")}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline">{formatRole(employee.role)}</Badge>
                    {isAdminHQ && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setEditingRoleId(employee.id)}
                      >
                        <Pen className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>{getStoreName(employee.storeId)}</TableCell>
              <TableCell>{formatDate(employee.createdAt)}</TableCell>
              <TableCell>{employee.address || t("notProvided")}</TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteEmployee(employee)}
                  className="text-red-500 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
